import { derived, get, writable } from 'svelte/store';

import { DomainsService } from '@client/services/domains';

import { billingId } from '@client/stores/domain-settings';
import { userStore } from '@client/stores/user';

import { escapeRegExp } from '@common/utils/strings';

// Shared by both switcher menus. Only the first page is held; search and paging stay server-side.

export const DOMAINS_PER_PAGE = 50;

const MDM = 'mdm';
const BILLING = 'billing';
const ALL = 'all';

const emptySection = () => ({ items: [], page: 0, total: 0, loading: false, error: false, loaded: false });

const sections = {
  [MDM]: writable(emptySection()),
  [BILLING]: writable(emptySection()),
  [ALL]: writable(emptySection())
};

const query = writable('');

// Requests are not cancellable, so superseded ones are ignored by generation.
const generation = { [MDM]: 0, [BILLING]: 0, [ALL]: 0 };

export const mdmDomains = { subscribe: sections[MDM].subscribe };
export const billingDomains = { subscribe: sections[BILLING].subscribe };
export const allDomains = { subscribe: sections[ALL].subscribe };
export const domainQuery = { subscribe: query.subscribe };

export const canSwitchDomains = derived(userStore, ($user) => $user?.domaincount >= 1 || Boolean($user?.isStaff));

export const hasBillingDomains = derived([userStore, billingId], ([$user, $billingId]) =>
  Boolean($user?.isStaff && $billingId)
);

const requestFor = (name, page) => {
  const body = {
    debounceBillingid: '',
    debounceQuery: '',
    page,
    perPage: DOMAINS_PER_PAGE,
    // The backend compiles this with new RegExp.
    query: escapeRegExp(get(query).trim())
  };

  if (name === BILLING) {
    return { ...body, billingid: get(billingId) || '', screen: 'staff' };
  }

  // Wildcard-staff scope. Without a staff record the backend returns the ordinary allowed domains.
  if (name === ALL) {
    return { ...body, billingid: '', screen: 'staff' };
  }

  return { ...body, billingid: '', screen: '' };
};

const load = async (name, { reset = false } = {}) => {
  const store = sections[name];
  const current = get(store);

  // Paging is serialized, but a reset must be able to supersede an in-flight page.
  if (current.loading && !reset) {
    return;
  }

  const id = ++generation[name];
  const page = reset ? 1 : current.page + 1;

  store.set({ ...current, items: reset ? [] : current.items, loading: true, error: false });

  const response = await DomainsService.getAll(requestFor(name, page));

  if (id !== generation[name]) {
    return;
  }

  // getAll normalizes failures to {data: [], headers: {}}, so status must be read before data.
  if (response?.status >= 400) {
    store.update((state) => ({ ...state, loading: false, error: true }));
    return;
  }

  // A cancelled request resolves with no status and no headers: neither empty nor an error.
  const total = Number(response?.headers?.['x-filtered-total']);

  if (!Number.isFinite(total)) {
    store.set(emptySection());
    return;
  }

  const merged = reset ? [] : [...get(store).items];
  const seen = new Set(merged.map(({ domainname }) => domainname));

  (response.data || []).forEach((domain) => {
    if (domain?.domainname && !seen.has(domain.domainname)) {
      seen.add(domain.domainname);
      merged.push(domain);
    }
  });

  store.set({ items: merged, page, total, loading: false, error: false, loaded: true });
};

const resetSection = (name) => {
  generation[name] += 1;
  sections[name].set(emptySection());
};

/** Loads the first page of every section that applies to the current session. */
export const refreshDomainList = () => {
  query.set('');
  resetSection(MDM);

  if (get(canSwitchDomains)) {
    load(MDM, { reset: true });
  }

  resetSection(BILLING);
  resetSection(ALL);
};

export const searchDomains = (value) => {
  query.set(value);

  load(MDM, { reset: true });

  if (get(hasBillingDomains)) {
    load(BILLING, { reset: true });
  }

  // Thousands of domains, so the global scope is search-only.
  if (get(userStore)?.isStaff && value.trim()) {
    load(ALL, { reset: true });
  } else {
    resetSection(ALL);
  }
};

export const loadMoreDomains = (name) => load(name);
export const retryDomains = (name) => load(name, { reset: true });

export const DOMAIN_SECTIONS = { MDM, BILLING, ALL };

// billingId resolves after login and resets while switching.
hasBillingDomains.subscribe((enabled) => {
  if (enabled) {
    load(BILLING, { reset: true });
  } else {
    resetSection(BILLING);
  }
});
